const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const isTenantPublicUuid = value =>
  UUID_REGEX.test(String(value || "").trim())

export const isLegacyPublicLang = value =>
  ["en", "sa", "ar"].includes(String(value || "").trim().toLowerCase())

export const buildTenantPublicPageUrl = (tenantUuid, pagePath) => {
  if (typeof window === "undefined") {
    return `/${tenantUuid}/${pagePath}`
  }
  return `${window.location.origin}/${tenantUuid}/${pagePath}`
}

/** @param {"customer"|"rider"} [appType="customer"] */
export const buildTenantPrivacyPolicyUrl = (
  tenantUuid,
  appType = "customer"
) =>
  buildTenantPublicPageUrl(
    tenantUuid,
    appType === "rider" ? "rider/privacy-policy" : "privacy-policy"
  )

/** @param {"customer"|"rider"} [appType="customer"] */
export const buildTenantTermsUrl = (tenantUuid, appType = "customer") =>
  buildTenantPublicPageUrl(
    tenantUuid,
    appType === "rider"
      ? "rider/terms-and-conditions"
      : "terms-and-conditions"
  )

const INTRO_NOTE_REGEX =
  /^(\s*<p[^>]*>[\s\S]*?(?:Arabic version|privacy notice template)[\s\S]*?<\/p>\s*)+/i

const INLINE_COLOR_REGEX = /\bcolor\s*:\s*[^;]+;?/gi

/** Quill inline colors are often white and invisible on the public white panel. */
export const stripQuillInlineColors = html => {
  if (!html || typeof html !== "string") {
    return html || ""
  }

  return html
    .replace(/\sstyle="([^"]*)"/gi, (_match, styles) => {
      const cleaned = styles
        .replace(INLINE_COLOR_REGEX, "")
        .replace(/;\s*;/g, ";")
        .trim()
        .replace(/^;|;$/g, "")
      return cleaned ? ` style="${cleaned}"` : ""
    })
    .replace(/\sstyle=""\s*/g, " ")
}

export const sanitizePublicPageContent = html => {
  if (!html || typeof html !== "string") {
    return html || ""
  }

  return stripQuillInlineColors(html.replace(INTRO_NOTE_REGEX, "").trimStart())
}

/** API may return a document object, nested pageContent, or a single-item array. */
export const normalizePublicPagePayload = data => {
  if (!data) return null
  if (Array.isArray(data)) {
    return data[0] || null
  }
  if (typeof data !== "object") {
    return null
  }
  if (Array.isArray(data.data)) {
    return data.data[0] || null
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return normalizePublicPagePayload(data.data)
  }
  if (
    data.pageContent &&
    typeof data.pageContent === "object" &&
    !Array.isArray(data.pageContent)
  ) {
    return { ...data.pageContent, ...data }
  }
  return data
}

const isHtmlFieldEmpty = html => {
  if (html == null) return true
  const text = String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim()
  return text.length === 0
}

const toHtmlField = value => {
  if (value == null) {
    return ""
  }
  const html = typeof value === "string" ? value : String(value)
  return isHtmlFieldEmpty(html) ? "" : html
}

const isRtlHtmlBlock = element => {
  const dir = String(element.getAttribute("dir") || "").toLowerCase()
  const cls = element.getAttribute("class") || ""
  const align = String(element.getAttribute("align") || "").toLowerCase()
  return (
    dir === "rtl" ||
    /\bql-align-right\b/.test(cls) ||
    align === "right"
  )
}

/** When EN/AR were saved in one Quill field, show only blocks for the active locale. */
export const filterPublicPageHtmlByLocale = (html, isArabic) => {
  if (!html || typeof html !== "string") {
    return ""
  }
  if (typeof document === "undefined") {
    return html
  }

  try {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const blocks = [...doc.body.children]
    if (blocks.length <= 1) {
      return html
    }

    const matching = blocks.filter(block =>
      isArabic ? isRtlHtmlBlock(block) : !isRtlHtmlBlock(block)
    )
    if (matching.length === 0) {
      return html
    }
    return matching.map(block => block.outerHTML).join("")
  } catch {
    return html
  }
}

export const pickPublicPageHtml = (pageContent, isArabic) => {
  if (!pageContent) {
    return ""
  }

  const primary = toHtmlField(
    isArabic ? pageContent.descriptionAr : pageContent.description
  )
  const fallback = toHtmlField(
    isArabic ? pageContent.description : pageContent.descriptionAr
  )
  let raw = primary || fallback || ""

  // Only split mixed RTL/LTR blocks when using the opposite-locale fallback.
  if (!primary && fallback) {
    raw = filterPublicPageHtmlByLocale(raw, isArabic)
  }

  return sanitizePublicPageContent(raw)
}
